import type { Device as ApiDevice } from "@teamtable/api";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useParams } from "wouter";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { isFleetAdmin } from "../hooks/useFleetAccess.js";
import { useFleetOptions } from "../hooks/useFleetOptions.js";
import { formatUkDate, formatUkDateTime } from "../lib/dates.js";
import { DeviceCommands } from "./DeviceCommands.js";
import { DeviceConfigEditor } from "./DeviceConfigEditor.js";
import { DeviceHardware } from "./DeviceHardware.js";
import { DeviceKioskUpdate } from "./DeviceKioskUpdate.js";
import { DeviceNetworkTest } from "./DeviceNetworkTest.js";

interface Device {
  id: string;
  fleet_id?: string;
  venue_id?: string;
  serial_number: string;
  name: string;
  status: string;
  last_heartbeat_at?: string;
  game_settings_enabled?: boolean;
  hardware_version?: string | null;
  content_state?: Record<string, unknown>;
  hardware_serial?: string;
  model?: string;
  android_version?: string;
  ip_address?: string;
  dpc_version?: string;
  dpc_version_code?: number;
  app_url?: string;
  wifi?: ApiDevice["wifi"];
  wifi_updated_at?: string;
  internet_speed?: ApiDevice["internet_speed"];
  soc_temp_celsius?: number;
  temperature_updated_at?: string;
  applied_config?: Record<string, unknown>;
  applied_config_updated_at?: string;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface Venue {
  id: string;
  fleet_id: string;
  name: string;
  slug: string;
}

interface EnrollmentLogEntry {
  id: string;
  event: string;
  details: Record<string, unknown>;
  ip_address?: string;
  created_at: string;
}

interface DeviceEvent {
  id: string;
  event_name: string;
  properties: Record<string, unknown>;
  client_timestamp: string;
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function statusBadge(status: string) {
  const colors: Record<string, string> = {
    online: "bg-green-500/20 text-green-400",
    active: "bg-green-500/20 text-green-400",
    provisioned: "bg-blue-500/20 text-blue-400",
    pending: "bg-yellow-500/20 text-yellow-400",
    rejected: "bg-red-500/20 text-red-400",
    offline: "bg-gray-500/20 text-gray-400",
  };
  const cls = colors[status] ?? "bg-gray-500/20 text-gray-400";
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div>
      <dt className="text-xs text-text-muted">{label}</dt>
      <dd className="text-sm text-text-secondary font-mono">{value ?? "\u2014"}</dd>
    </div>
  );
}

function CollapsibleSection({
  title,
  count,
  defaultOpen = false,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="bg-surface-raised border border-border rounded-lg p-6">
      <button
        type="button"
        data-testid="collapsible-section-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 text-left text-lg font-semibold text-text-primary hover:text-accent transition-colors"
      >
        <span aria-hidden="true" className="text-text-muted text-sm w-3">
          {open ? "▾" : "▸"}
        </span>
        <span>{title}</span>
        {count !== undefined && (
          <span className="text-sm font-normal text-text-muted">({count})</span>
        )}
      </button>
      {open && <div className="mt-4">{children}</div>}
    </section>
  );
}

export function DeviceDetail() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const { options: fleetOptions } = useFleetOptions();

  const [device, setDevice] = useState<Device | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [enrollmentLog, setEnrollmentLog] = useState<EnrollmentLogEntry[]>([]);
  const [events, setEvents] = useState<DeviceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const canAdmin = user
    ? device?.fleet_id
      ? isFleetAdmin(user, device.fleet_id)
      : user.is_platform_admin
    : false;
  const isPlatformAdmin = user?.is_platform_admin ?? false;

  // Every editable field is a draft laid over what the server last told us: null
  // means untouched and the device's own value shows through. Refetching the
  // device therefore never overwrites an edit that is still in progress.
  const [fleetDraft, setFleetDraft] = useState<string | null>(null);
  const [fleetSaving, setFleetSaving] = useState(false);
  const [fleetMessage, setFleetMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const selectedFleetId = fleetDraft ?? device?.fleet_id ?? "";

  const [venueDraft, setVenueDraft] = useState<string | null>(null);
  const [venueSaving, setVenueSaving] = useState(false);
  const [venueMessage, setVenueMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const selectedVenueId = venueDraft ?? device?.venue_id ?? "";

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [nameSaving, setNameSaving] = useState(false);
  const nameValue = nameDraft ?? device?.name ?? "";

  const [configDraft, setConfigDraft] = useState<string | null>(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [configMessage, setConfigMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const configText = configDraft ?? (device ? JSON.stringify(device.config, null, 2) : "");

  const fetchDevice = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}"]["get"]({
        parameters: { id },
        body: null,
      });
      if (res.code === 200) {
        setDevice(res.body as Device);
      } else if (res.code === 404) {
        setNotFound(true);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchVenues = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/venues"]["get"]({
        parameters: {},
        body: null,
      });
      if (res.code === 200) {
        setVenues(res.body as Venue[]);
      }
    } catch {
      // Ignore
    }
  }, []);

  // The endpoint is platform-only; a fleet admin asking would take a 403.
  const fetchEnrollmentLog = useCallback(async () => {
    if (!id || !isPlatformAdmin) return;
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}/enrollment-log"]["get"]({
        parameters: { id },
        body: null,
      });
      if (res.code === 200) {
        setEnrollmentLog(res.body as EnrollmentLogEntry[]);
      }
    } catch {
      // Ignore
    }
  }, [id, isPlatformAdmin]);

  const fetchEvents = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}/events"]["get"]({
        parameters: { id, page: 1, per_page: 25 },
        body: null,
      });
      if (res.code === 200) {
        const body = res.body as { data: DeviceEvent[] };
        setEvents(body.data);
      }
    } catch {
      // Ignore
    }
  }, [id]);

  useEffect(() => {
    fetchDevice();
    fetchVenues();
    fetchEnrollmentLog();
    fetchEvents();
  }, [fetchDevice, fetchVenues, fetchEnrollmentLog, fetchEvents]);

  useEffect(() => {
    if (!device || window.location.hash !== "#content-bundle") return;
    document.getElementById("content-bundle")?.scrollIntoView({ block: "start" });
  }, [device?.id]);

  const handleFleetSave = useCallback(async () => {
    if (!id) return;
    setFleetSaving(true);
    setFleetMessage(null);
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}/fleet"]["put"]({
        parameters: { id },
        body: { fleet_id: selectedFleetId || null },
      });
      if (res.code === 200) {
        setDevice(res.body as Device);
        setFleetDraft(null);
        setFleetMessage({ type: "success", text: "Fleet updated" });
        fetchVenues();
        fetchEnrollmentLog();
      } else {
        const body = res.body as { message?: string };
        setFleetMessage({
          type: "error",
          text: body.message ?? "Failed to update fleet",
        });
      }
    } catch {
      setFleetMessage({ type: "error", text: "Network error" });
    } finally {
      setFleetSaving(false);
    }
  }, [id, selectedFleetId, fetchVenues, fetchEnrollmentLog]);

  const handleVenueSave = useCallback(async () => {
    if (!id) return;
    setVenueSaving(true);
    setVenueMessage(null);
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}"]["patch"]({
        parameters: { id },
        body: { venue_id: selectedVenueId || null },
      });
      if (res.code === 200) {
        setDevice(res.body as Device);
        setVenueDraft(null);
        setVenueMessage({ type: "success", text: "Venue updated" });
        fetchEnrollmentLog();
      } else {
        const body = res.body as { message?: string };
        setVenueMessage({
          type: "error",
          text: body.message ?? "Failed to update venue",
        });
      }
    } catch {
      setVenueMessage({ type: "error", text: "Network error" });
    } finally {
      setVenueSaving(false);
    }
  }, [id, selectedVenueId, fetchEnrollmentLog]);

  const handleNameSave = useCallback(async () => {
    if (!id || !nameValue.trim()) return;
    setNameSaving(true);
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}"]["patch"]({
        parameters: { id },
        body: { name: nameValue.trim() },
      });
      if (res.code === 200) {
        setDevice(res.body as Device);
        setNameDraft(null);
        setEditingName(false);
      }
    } catch {
      // Ignore
    } finally {
      setNameSaving(false);
    }
  }, [id, nameValue]);

  const handleConfigSave = useCallback(async () => {
    if (!id || !device) return;
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(configText);
    } catch {
      setConfigMessage({ type: "error", text: "Invalid JSON" });
      return;
    }
    // The editor holds the whole config document, but the endpoint is a JSON
    // merge patch: a key the user removed has to be sent as an explicit null
    // for the removal to land.
    const body: Record<string, unknown> = { ...parsed };
    for (const key of Object.keys(device.config)) {
      if (!(key in parsed)) {
        body[key] = null;
      }
    }
    setConfigSaving(true);
    setConfigMessage(null);
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}/config"]["patch"]({
        parameters: { id },
        body,
      });
      if (res.code === 200) {
        setDevice(res.body as Device);
        setConfigDraft(null);
        setConfigMessage({ type: "success", text: "Config updated" });
      } else {
        const body = res.body as { message?: string };
        setConfigMessage({
          type: "error",
          text: body.message ?? "Failed to update config",
        });
      }
    } catch {
      setConfigMessage({ type: "error", text: "Network error" });
    } finally {
      setConfigSaving(false);
    }
  }, [id, device, configText]);

  if (loading) {
    return (
      <main className="p-6">
        <p className="text-text-muted text-sm">Loading device...</p>
      </main>
    );
  }

  if (notFound || !device) {
    return (
      <main className="p-6">
        <div className="mb-6">
          <a
            href="/devices"
            className="text-sm text-text-muted hover:text-accent transition-colors no-underline"
          >
            &larr; Back to Devices
          </a>
        </div>
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">Device not found.</p>
        </div>
      </main>
    );
  }

  const venueChanged = (selectedVenueId || undefined) !== device.venue_id;

  return (
    <main className="p-6 max-w-4xl">
      <div className="mb-6">
        <a
          href="/devices"
          className="text-sm text-text-muted hover:text-accent transition-colors no-underline"
        >
          &larr; Back to Devices
        </a>
      </div>

      {/* Header */}
      <div className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3">
          {canAdmin && editingName ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                type="text"
                value={nameValue}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleNameSave();
                  if (e.key === "Escape") {
                    setEditingName(false);
                    setNameDraft(null);
                  }
                }}
                className="text-2xl font-bold bg-surface border border-border rounded px-2 py-1 text-text-primary focus:outline-none focus:border-accent flex-1"
                autoFocus
              />
              <button
                onClick={handleNameSave}
                disabled={nameSaving || !nameValue.trim()}
                className="px-3 py-1.5 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                {nameSaving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => {
                  setEditingName(false);
                  setNameDraft(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <>
              <h1
                className={`text-2xl font-bold text-text-primary ${canAdmin ? "cursor-pointer hover:text-accent transition-colors" : ""}`}
                onClick={canAdmin ? () => setEditingName(true) : undefined}
                title={canAdmin ? "Click to edit name" : undefined}
              >
                {device.name}
              </h1>
              {statusBadge(device.status)}
            </>
          )}
        </div>
      </div>

      {/* Venue Assignment — directly under the device name */}
      <section className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">📍 Venue Assignment</h2>
        {canAdmin ? (
          device.fleet_id ? (
            <>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-sm text-text-secondary mb-1">Venue</label>
                  <select
                    value={selectedVenueId}
                    onChange={(e) => {
                      setVenueDraft(e.target.value);
                      setVenueMessage(null);
                    }}
                    aria-label="Venue"
                    className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors"
                  >
                    <option value="">Unassigned</option>
                    {venues
                      .filter((v) => v.fleet_id === device.fleet_id)
                      .map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                  </select>
                </div>
                <button
                  onClick={handleVenueSave}
                  disabled={venueSaving || !venueChanged}
                  className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {venueSaving ? "Saving..." : "Save"}
                </button>
              </div>
              {venueMessage && (
                <p
                  className={`text-sm mt-2 ${venueMessage.type === "success" ? "text-green-400" : "text-red-400"}`}
                >
                  {venueMessage.text}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-text-secondary">
              Assign this device to a fleet before choosing a venue.
            </p>
          )
        ) : (
          <p className="text-sm text-text-secondary">
            {device.venue_id
              ? (venues.find((v) => v.id === device.venue_id)?.name ?? "Unknown")
              : "Unassigned"}
          </p>
        )}
      </section>

      {/* Device Info */}
      <section className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">📱 Device Info</h2>
        <DeviceHardware
          key={device.id}
          id={device.id}
          version={device.hardware_version}
          canEdit={isPlatformAdmin}
          onSaved={fetchDevice}
        />
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoRow label="Serial Number" value={device.serial_number} />
          <InfoRow label="Hardware Serial" value={device.hardware_serial} />
          <InfoRow label="Model" value={device.model} />
          <InfoRow label="Android Version" value={device.android_version} />
          <InfoRow label="IP Address" value={device.ip_address} />
          <InfoRow
            label="Kiosk Version"
            value={
              device.dpc_version
                ? device.dpc_version_code !== undefined
                  ? `${device.dpc_version} (code ${device.dpc_version_code})`
                  : device.dpc_version
                : undefined
            }
          />
          <InfoRow label="App URL" value={device.app_url} />
          <InfoRow
            label="Last Heartbeat"
            value={device.last_heartbeat_at ? timeAgo(device.last_heartbeat_at) : undefined}
          />
          <InfoRow label="Created" value={formatUkDate(device.created_at)} />
          <InfoRow label="Updated" value={formatUkDate(device.updated_at)} />
        </dl>
      </section>

      <section className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">📶 Wi-Fi</h2>
        {device.wifi ? (
          <>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <InfoRow
                label="Active connection"
                value={device.wifi.connected ? "Wi-Fi" : "Not using Wi-Fi"}
              />
              <InfoRow
                label="Signal strength (RSSI)"
                value={
                  device.wifi.rssi_dbm !== undefined ? `${device.wifi.rssi_dbm} dBm` : undefined
                }
              />
              <InfoRow
                label="Signal level"
                value={
                  device.wifi.signal_level !== undefined
                    ? `${device.wifi.signal_level} / 4`
                    : undefined
                }
              />
              <InfoRow
                label="Wi-Fi Tx link rate"
                value={
                  device.wifi.tx_link_speed_mbps !== undefined
                    ? `${device.wifi.tx_link_speed_mbps} Mbps`
                    : undefined
                }
              />
              <InfoRow
                label="Frequency"
                value={
                  device.wifi.frequency_mhz !== undefined
                    ? `${device.wifi.frequency_mhz} MHz`
                    : undefined
                }
              />
              <InfoRow
                label="Last Wi-Fi report"
                value={
                  device.wifi_updated_at ? formatUkDateTime(device.wifi_updated_at) : undefined
                }
              />
            </dl>
            <p className="text-sm text-text-muted mt-4">
              Last reported Wi-Fi signal and connection details. Tx link rate is the device’s
              current radio rate to the access point. Actual transfer speeds will be lower. Measured
              download and upload speeds are shown in the Internet speed section below.
            </p>
          </>
        ) : (
          <p className="text-sm text-text-muted">No Wi-Fi diagnostics reported yet.</p>
        )}
      </section>

      <section className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">🚀 Internet speed</h2>
        {device.internet_speed ? (
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoRow
              label="Download"
              value={`${device.internet_speed.download_mbps.toFixed(2)} Mbps`}
            />
            <InfoRow
              label="Upload"
              value={`${device.internet_speed.upload_mbps.toFixed(2)} Mbps`}
            />
            <InfoRow
              label="Last speed test"
              value={formatUkDateTime(device.internet_speed.tested_at)}
            />
          </dl>
        ) : (
          <p className="text-sm text-text-muted">No internet speed test reported yet.</p>
        )}
        <DeviceNetworkTest
          deviceId={device.id}
          canRun={!!canAdmin}
          onCompleted={fetchDevice}
          deviceVersionCode={device.dpc_version_code}
        />
      </section>

      <section className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">🌡️ Temperature</h2>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoRow
            label="SoC temperature"
            value={
              device.soc_temp_celsius !== undefined
                ? `${device.soc_temp_celsius.toFixed(1)} °C`
                : undefined
            }
          />
          <InfoRow
            label="Last temperature report"
            value={
              device.temperature_updated_at
                ? formatUkDateTime(device.temperature_updated_at)
                : undefined
            }
          />
        </dl>
      </section>

      {/* Fleet Assignment */}
      <section className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">🚢 Fleet Assignment</h2>
        {isPlatformAdmin ? (
          <>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm text-text-secondary mb-1">Fleet</label>
                <select
                  value={selectedFleetId}
                  onChange={(e) => {
                    setFleetDraft(e.target.value);
                    setFleetMessage(null);
                  }}
                  aria-label="Fleet"
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors"
                >
                  <option value="">Unassigned</option>
                  {fleetOptions.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleFleetSave}
                disabled={fleetSaving || (selectedFleetId || undefined) === device.fleet_id}
                className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {fleetSaving ? "Saving..." : "Save"}
              </button>
            </div>
            {fleetMessage && (
              <p
                className={`text-sm mt-2 ${fleetMessage.type === "success" ? "text-green-400" : "text-red-400"}`}
              >
                {fleetMessage.text}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-text-secondary">
            {device.fleet_id
              ? (fleetOptions.find((f) => f.id === device.fleet_id)?.name ?? "Unknown")
              : "Unassigned"}
          </p>
        )}
      </section>

      {/* Configuration (platform admins only) */}
      {isPlatformAdmin && (
        <section className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">⚙️ Configuration</h2>

          <DeviceConfigEditor
            gameSettingsEnabled={device.game_settings_enabled}
            configText={configText}
            onConfigTextChange={setConfigDraft}
            onSave={handleConfigSave}
            saving={configSaving}
            message={configMessage}
            onMessageClear={() => setConfigMessage(null)}
          />

          <div className="mt-6 pt-4 border-t border-border">
            <h3 className="text-sm font-medium mb-2">Desired and Reported Display Settings</h3>
            <p className="text-sm text-text-secondary">
              Rotation: desired {String(device.config.screen_rotation ?? 0)}°, reported{" "}
              {String(device.applied_config?.screen_rotation ?? "unknown")}°
            </p>
            <p className="text-sm text-text-secondary">
              Brightness: desired {String(device.config.screen_brightness ?? 80)}%, reported{" "}
              {String(device.applied_config?.screen_brightness ?? "unknown")}%
            </p>
            <p className="text-xs text-text-muted">
              Last display report:{" "}
              {device.applied_config_updated_at
                ? timeAgo(device.applied_config_updated_at)
                : "unknown"}
              . Reports may be stale; saving does not confirm application.
            </p>
            <h3 className="text-sm font-medium text-text-secondary mb-2 mt-4">Applied Config</h3>
            {device.applied_config ? (
              <pre className="px-3 py-2 rounded-md bg-surface border border-border text-text-secondary font-mono text-sm overflow-x-auto">
                {JSON.stringify(device.applied_config, null, 2)}
              </pre>
            ) : (
              <p className="text-sm text-text-muted">No config applied yet.</p>
            )}
          </div>
        </section>
      )}

      {/* Kiosk app update (platform admins only) */}
      {isPlatformAdmin && (
        <DeviceKioskUpdate
          deviceId={device.id}
          deviceName={device.name}
          installedVersionCode={device.dpc_version_code}
          installedVersionName={device.dpc_version}
          onInstalledVersionChanged={fetchDevice}
        />
      )}

      {/* Remote Commands (platform admins only) */}
      {isPlatformAdmin && (
        <DeviceCommands
          deviceId={device.id}
          deviceName={device.name}
          serialNumber={device.serial_number}
        />
      )}

      {/* Enrollment Log (platform admins only) */}
      {isPlatformAdmin && (
        <section className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">📋 Enrollment Log</h2>
          {enrollmentLog.length === 0 ? (
            <p className="text-sm text-text-muted">No enrollment events.</p>
          ) : (
            <div className="space-y-3">
              {enrollmentLog.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-start justify-between border-b border-border/50 pb-3 last:border-0"
                >
                  <div>
                    <div className="font-medium text-text-primary text-sm">{entry.event}</div>
                    <div className="text-xs text-text-muted mt-0.5 font-mono max-w-lg truncate">
                      {Object.entries(entry.details)
                        .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
                        .join(", ")}
                    </div>
                    {entry.ip_address && (
                      <div className="text-xs text-text-muted mt-0.5">IP: {entry.ip_address}</div>
                    )}
                  </div>
                  <span className="text-xs text-text-muted whitespace-nowrap ml-4">
                    {formatUkDateTime(entry.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <CollapsibleSection title="📜 Recent Events" count={events.length}>
        {events.length === 0 ? (
          <p className="text-sm text-text-muted">No events recorded.</p>
        ) : (
          <>
            <div className="space-y-2">
              {events.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between bg-surface border border-border rounded-lg px-4 py-3"
                >
                  <div>
                    <div className="font-medium text-text-primary font-mono text-sm">
                      {e.event_name}
                    </div>
                    <div className="text-xs text-text-muted mt-0.5 max-w-lg truncate">
                      {Object.entries(e.properties).length > 0
                        ? Object.entries(e.properties)
                            .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
                            .join(", ")
                        : "\u2014"}
                    </div>
                  </div>
                  <span className="text-sm text-text-muted whitespace-nowrap ml-4">
                    {formatUkDateTime(e.client_timestamp)}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <a
                href={`/devices/${id}/events`}
                className="text-sm text-accent hover:underline no-underline"
              >
                View all events &rarr;
              </a>
            </div>
          </>
        )}
      </CollapsibleSection>
    </main>
  );
}
